// Tab Hiệu lực & rà soát: lịch hết / bắt đầu hiệu lực 90 ngày tới, tuổi tài liệu, danh sách cần rà soát định kỳ,
// danh mục chưa có tài liệu hiệu lực, các lần ban hành lại trong kỳ.
import { Button, Modal, Table, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, CheckCheck, FilePlus2, RefreshCcw } from 'lucide-react';
import { deptName } from '../data';
import { categoryPath } from '../logic';
import { HORIZON_DAYS, REVIEW_MONTHS, type lifecycleAnalytics } from '../analytics';
import useDocsStore from '../store';
import useDocActions from '../components/useDocActions';
import { docLink, fmtDate, useDocAuth } from '../utils';
import { COLORS, Card, Legend, Stat, StatGrid, type DashCtx } from './ui';

export default function LifecycleTab({ ctx, data }: { ctx: DashCtx; data: ReturnType<typeof lifecycleAnalytics> }) {
  const navigate = useNavigate();
  const { me, can } = useDocAuth();
  const { perm } = useDocActions();
  const reviewDoc = useDocsStore(s => s.reviewDoc);
  const [modal, modalCtx] = Modal.useModal();
  const maxAge = Math.max(1, ...data.ageBuckets.map(b => b.count));

  const keep = (id: string, code: string) => modal.confirm({
    title: `Xác nhận rà soát ${code}?`,
    content: 'Tài liệu vẫn phù hợp và được giữ nguyên nội dung. Mốc rà soát tiếp theo sau 12 tháng.',
    okText: 'Giữ nguyên', cancelText: 'Hủy',
    onOk: () => { reviewDoc(id, me); message.success('Đã ghi nhận rà soát'); },
  });

  return (
    <>
      {modalCtx}
      <StatGrid>
        <Stat label="Đang hiệu lực" value={data.active} sub={`${data.noExpiry} không thời hạn`} onClick={() => ctx.goList({ tab: 'active' })} />
        <Stat label="Tuổi trung bình" value={`${data.avgAgeMonths.toFixed(1).replace('.', ',')} tháng`} sub="kể từ ngày có hiệu lực" />
        <Stat label="Cần rà soát" value={data.review.length} tone={data.review.length ? 'text-amber-600' : 'text-slate-800'} sub={`quá ${REVIEW_MONTHS} tháng chưa cập nhật`}
          info={`Tài liệu còn hiệu lực đã ban hành hoặc rà soát lần cuối hơn ${REVIEW_MONTHS} tháng trước.`} onClick={() => document.getElementById('review-list')?.scrollIntoView({ behavior: 'smooth' })} />
        <Stat label={`Hết hiệu lực trong ${HORIZON_DAYS} ngày`} value={data.expiringSoon} tone={data.expiring30 ? 'text-red-500' : 'text-slate-800'} sub={`${data.expiring30} trong 30 ngày tới`} onClick={() => ctx.goList({ flag: 'expiring' })} />
        <Stat label="Danh mục trống" value={data.emptyCats.length} sub="chưa có tài liệu hiệu lực" onClick={() => document.getElementById('empty-cats')?.scrollIntoView({ behavior: 'smooth' })} />
      </StatGrid>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card title={`Lịch hiệu lực ${HORIZON_DAYS} ngày tới`} hint="theo tuần" className="lg:col-span-3">
          <Legend items={[{ label: 'Hết hiệu lực', color: COLORS.red }, { label: 'Bắt đầu hiệu lực', color: COLORS.blue }]} />
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weeks} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} interval={0} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                <ChartTooltip labelFormatter={l => `Tuần từ ${l}`} formatter={(v, n) => [`${v} tài liệu`, n === 'expiring' ? 'Hết hiệu lực' : 'Bắt đầu hiệu lực']} />
                <Bar dataKey="expiring" stackId="a" fill={COLORS.red} barSize={20} />
                <Bar dataKey="starting" stackId="a" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Sắp diễn ra" className="lg:col-span-2">
          {data.timeline.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Không có thay đổi hiệu lực trong {HORIZON_DAYS} ngày tới</div> : (
            <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto pr-1">
              {data.timeline.map(t => (
                <li key={`${t.kind}-${t.doc.id}`} className="flex items-center gap-3 py-2 text-[13px]">
                  <div className={`w-12 shrink-0 rounded-md py-1 text-center ${t.kind === 'expire' ? 'bg-red-50 text-red-600' : 'bg-sky-50 text-sky-700'}`}>
                    <div className="text-sm font-bold leading-none">{fmtDate(t.date).slice(0, 2)}</div>
                    <div className="text-[10px]">th{fmtDate(t.date).slice(3, 5)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={docLink(t.doc.id)} className="block truncate font-medium text-slate-700 hover:text-[#357f96]">{t.doc.title}</Link>
                    <div className="text-xs text-slate-400">{t.kind === 'expire' ? 'Hết hiệu lực' : 'Bắt đầu hiệu lực'} · {t.doc.code}</div>
                  </div>
                  {t.kind === 'expire' && perm.reissue(t.doc) && (
                    <Button size="small" icon={<RefreshCcw size={13} />} onClick={() => navigate(`/add-new-document?reissue=${t.doc.id}`)}>Ban hành lại</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title={<span id="review-list">Cần rà soát định kỳ</span>} hint={`đã ${REVIEW_MONTHS}+ tháng`} className="xl:col-span-2">
          <Table
            size="small" rowKey={r => r.doc.id} dataSource={data.review} scroll={{ x: 640 }}
            pagination={{ pageSize: 6, hideOnSinglePage: true, size: 'small' }}
            locale={{ emptyText: 'Không có tài liệu quá hạn rà soát' }}
            columns={[
              { title: 'Tài liệu', key: 'doc', render: (_v, r) => <div className="min-w-0"><Link to={docLink(r.doc.id)} className="font-medium text-[#357f96] hover:underline">{r.doc.code}</Link><div className="truncate text-xs text-slate-500">{r.doc.title}</div></div> },
              { title: 'Đơn vị', key: 'unit', width: 140, render: (_v, r) => <span className="text-xs text-slate-600">{deptName(r.doc.unitId)}</span> },
              { title: 'Cập nhật cuối', key: 'last', width: 120, render: (_v, r) => <div className="text-xs"><div>{fmtDate(r.doc.reviewedAt || r.doc.approvedAt)}</div><div className="font-medium text-amber-600">{r.months} tháng trước</div></div> },
              {
                title: '', key: 'act', width: 200, align: 'right',
                render: (_v, r) => (
                  <div className="flex justify-end gap-1.5">
                    {can('edit') && <Button size="small" icon={<CheckCheck size={13} />} onClick={() => keep(r.doc.id, r.doc.code)}>Giữ nguyên</Button>}
                    {perm.reissue(r.doc) && <Button size="small" icon={<RefreshCcw size={13} />} onClick={() => navigate(`/add-new-document?reissue=${r.doc.id}`)}>Ban hành lại</Button>}
                  </div>
                ),
              },
            ]}
          />
        </Card>
        <Card title="Tuổi tài liệu đang hiệu lực">
          <div className="space-y-3 pt-1">
            {data.ageBuckets.map((b, i) => (
              <div key={b.label} className="grid grid-cols-[80px_1fr_32px] items-center gap-3 text-[13px]">
                <span className="text-slate-600">{b.label}</span>
                <span className="h-6 rounded bg-slate-50"><span className="block h-full rounded" style={{ width: `${(b.count / maxAge) * 100}%`, background: i >= 2 ? COLORS.amber : COLORS.brand }} /></span>
                <span className="text-right font-semibold text-slate-700">{b.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Tài liệu trên 1 năm nên được rà soát lại để đảm bảo vẫn phù hợp với quy định hiện hành.
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title={<span id="empty-cats">Danh mục chưa có tài liệu hiệu lực</span>} hint={`${data.emptyCats.length} danh mục`}>
          {data.emptyCats.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Mọi danh mục đều có tài liệu hiệu lực</div> : (
            <ul className="divide-y divide-slate-100">
              {data.emptyCats.map(c => (
                <li key={c.cat.id} className="flex items-center gap-3 py-2 text-[13px]">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-slate-700">{categoryPath(ctx.categories, c.cat.id)}</div>
                    <div className="text-xs text-slate-400">{c.total ? `${c.total} tài liệu nhưng không còn bản nào hiệu lực` : 'Chưa từng có tài liệu'}</div>
                  </div>
                  {can('create') && <Button size="small" icon={<FilePlus2 size={13} />} onClick={() => navigate(`/add-new-document?cat=${c.cat.id}`)}>Tạo tài liệu</Button>}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Ban hành lại trong kỳ" hint={`tổng cộng ${data.reissueTotal} lần`}>
          {data.reissues.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">Không có tài liệu ban hành lại trong kỳ</div> : (
            <ul className="divide-y divide-slate-100">
              {data.reissues.map(r => (
                <li key={r.doc.id} className="flex items-center gap-2 py-2 text-[13px]">
                  {r.old ? <Link to={docLink(r.old.id)} className="text-slate-400 line-through hover:text-slate-600">{r.old.code}</Link> : <span className="text-slate-300">—</span>}
                  <ArrowRight size={14} className="text-slate-300" />
                  <Link to={docLink(r.doc.id)} className="font-medium text-[#357f96] hover:underline">{r.doc.code}</Link>
                  <span className="ml-auto text-xs text-slate-400">{fmtDate(r.doc.approvedAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
